import { useState } from 'react';

import { useSearch } from '@tanstack/react-router';

import { CheckoutStepCompleted, CheckoutStepFailed } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { CHECKOUT_STEPS } from 'src/analytics/trackingTypes';
import { HTTPError } from 'src/api/createApiClient.types';
import type { CloudConnectAddress } from 'src/api/endpoints/cloudConnect.types';
import { useCheckoutPromotionPreview } from 'src/api/queries';

export const useCheckoutPromotion = () => {
  const initialCode = useSearch({ strict: false }).promotion_code;
  const [code, setCode] = useState(initialCode ?? '');
  const preview = useCheckoutPromotionPreview();
  const hasCode = code.trim().length > 0;

  return {
    code,
    hasCode,
    canContinue: !preview.isPending && (!hasCode || preview.isSuccess),
    setCode: (nextCode: string) => {
      setCode(nextCode);
      preview.reset();
    },
    reset: preview.reset,
    apply: (address: CloudConnectAddress) => {
      const promotionCode = code.trim();
      if (!promotionCode) {
        throw new Error('A promotion code is required for preview.');
      }
      preview.mutate(
        { promotionCode, address },
        {
          onSuccess: (data) => {
            // The coupon name is tracked rather than the customer-facing code.
            tracker.trackEvent(
              new CheckoutStepCompleted({
                checkout_step: CHECKOUT_STEPS.CHECKOUT_V2_PROMOTION_APPLIED,
                coupon: data.appliedPromotion.name,
              })
            );
          },
          onError: (error) => {
            tracker.trackEvent(
              new CheckoutStepFailed({
                checkout_step: CHECKOUT_STEPS.CHECKOUT_V2_PROMOTION_FAILED,
                error_code:
                  error instanceof HTTPError
                    ? String(error.response.status)
                    : 'unknown',
                error_name: error.name,
                error_message: error.message,
              })
            );
          },
        }
      );
    },
    isPending: preview.isPending,
    error: preview.error,
    // Reset detaches the mutation observer, so an older response cannot restore
    // a preview after the customer edits the code or pricing address.
    appliedPromotion: preview.isSuccess
      ? {
          code: preview.variables.promotionCode,
          address: preview.variables.address,
          preview: preview.data,
        }
      : undefined,
  };
};

export type CheckoutPromotion = ReturnType<typeof useCheckoutPromotion>;
